import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { UserContext } from '../UserContext';
import './CreateGroup.css';

const API_BASE_URL = 'https://wholeness-forward.onrender.com/api';

const CreateGroup = () => {
    const { user } = useContext(UserContext);

    const [groupName, setGroupName] = useState('');
    const [maxMembers, setMaxMembers] = useState(6);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [groupId, setGroupId] = useState(null);
    const [joinCode, setJoinCode] = useState('');
    const [adminName, setAdminName] = useState('');
    const [totalWeightLost, setTotalWeightLost] = useState(0);
    const [userWeight, setUserWeight] = useState(null);
    const [hasWeightUpdate, setHasWeightUpdate] = useState(false); // ✅ New check for weight updates

    useEffect(() => {
        const fetchUserWeight = async () => {
            if (!user || !user.token) return;

            try {
                
                const response = await axios.get(`${API_BASE_URL}/user/${user.id}`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });

                const userData = response.data;
                setUserWeight(userData.startingWeightG || 0);
                setHasWeightUpdate(userData.weightUpdates && userData.weightUpdates.length > 0); // ✅ Ensure user has at least one update

            } catch (error) {
                console.error("❌ Error fetching user weight:", error.response?.data || error);
            }
        };

        fetchUserWeight();
    }, [user, hasWeightUpdate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user || !user.token) {
            setMessage('You must be logged in to create a group.');
            return;
        }

        // ✅ Prevent group creation if no starting weight OR no updates
        if (userWeight === null || !hasWeightUpdate) {
            setMessage("⚠️ Please log your starting weight and at least one update before creating a group.");
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const response = await axios.post(
                `${API_BASE_URL}/group/create`,
                { name: groupName, maxMembers },
                { headers: { Authorization: `Bearer ${user.token}` } }
            );

            const { _id, joinCode, name, adminName, totalWeightLost } = response.data.group;

            setMessage(`🎉 Group "${name}" created by ${adminName}! Share this code: ${joinCode}`);
            setGroupId(_id);
            setJoinCode(joinCode);
            setAdminName(adminName);
            setTotalWeightLost(totalWeightLost);

        } catch (error) {
            setMessage(error.response?.data?.error || '❌ Failed to create group. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const copyJoinCode = () => {
        navigator.clipboard.writeText(joinCode);
        alert("📋 Join Code copied to clipboard!");
    };

    return (
        <div className="create-group-wrapper">
            <div className="create-group-container">
                <h2 className="create-group-title">Create a Group</h2>

                {message && <p className="create-group-message">{message}</p>}
                {loading && <p className="create-group-loading">Creating group...</p>}

                <form onSubmit={handleSubmit} className="create-group-form">
                    <label className="create-group-label">Group Name:</label>
                    <input 
                        type="text" 
                        value={groupName} 
                        onChange={(e) => setGroupName(e.target.value)} 
                        required 
                        disabled={loading}
                        className="create-group-input"
                    />

                    <label className="create-group-label">Group Size:</label>
                    <select 
                        value={maxMembers} 
                        onChange={(e) => setMaxMembers(Number(e.target.value))}
                        disabled={loading}
                        className="create-group-input"
                    >
                        <option value={6}>Up to 6 Members (Free)</option>
                        <option value={20}>Up to 20 Members ($2.99/month or $24.99/year)</option>
                        <option value={50}>Up to 50 Members ($4.99/month or $39.99/year)</option>
                    </select>

                    {/* ✅ Disable button if user lacks required weight data */}
                    <button type="submit" className="button" disabled={loading || userWeight === null || !hasWeightUpdate}>
                        {loading ? 'Creating...' : 'Create Group'}
                    </button>

                    {/* ✅ Show warning if requirements are not met */}
                    {userWeight === null || !hasWeightUpdate ? (
                        <p className="warning-text">
                            ⚠️ Please log your starting weight and at least one update in your profile before creating a group.
                        </p>
                    ) : null}
                </form>

                {joinCode && (
                    <div className="create-group-info">
                        <h3 className="create-group-info-title">🎉 Group Created!</h3>
                        <p><strong>Join Code:</strong> <span className="create-group-code">{joinCode}</span></p>
                        <button className="create-group-copy-button" onClick={copyJoinCode}>Copy Code</button>
                    </div>
                )}

                {adminName && <p><strong>Group Admin:</strong> {adminName}</p>}
                {groupId && <p><strong>Total Group Weight Lost:</strong> {totalWeightLost} lbs</p>}

                {groupId && (
                    <p>
                        <strong>Visit your group: </strong>
                        <Link to={`/group/${groupId}`} className="create-group-link">
                            Go to Group
                        </Link>
                    </p>
                )}
            </div>
        </div>
    );
};

export default CreateGroup;
